/**
 * Search by photo — the browser half, shared by the shop and Relay (worker/src/image-search.ts).
 *
 * The photo is shrunk HERE to ~768 px JPEG before it leaves the phone: a 4 MB camera shot becomes
 * ~100–200 KB, uploads in a second on mall Wi-Fi, and costs the vision model fewer image tokens.
 * The server refuses anything over 1.5 MB regardless. Nothing is kept: the blob is posted once and
 * dropped (the object URL used for the preview is revoked by the caller).
 */
export const PHOTO_MAX_PX = 768;
export const PHOTO_NOTE = "Your photo is only used for this search and isn't kept.";

/** A ~768 px JPEG of the chosen photo, or null when the browser cannot read it. */
export async function shrinkForSearch(file: Blob): Promise<Blob | null> {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, PHOTO_MAX_PX / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close();
    return await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.82));
  } catch {
    return null;
  }
}

export type PhotoRefusal = { ok: false; reason: string; message: string };

/** POSTs the shrunk photo; a network failure reads as the same friendly refusal as a busy server. */
export async function postPhoto<T>(path: string, blob: Blob, signal?: AbortSignal): Promise<({ ok: true } & T) | PhotoRefusal> {
  try {
    const r = await fetch(`/api${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "image/jpeg" },
      body: blob,
      signal,
    });
    const body = (await r.json().catch(() => null)) as (({ ok: true } & T) | PhotoRefusal) | null;
    if (body && typeof body === "object" && "ok" in body) return body;
    return { ok: false, reason: "busy", message: "Photo search is busy right now — try searching by name." };
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") throw err;
    return { ok: false, reason: "busy", message: "Photo search is busy right now — try searching by name." };
  }
}
